import { FC, useCallback, useContext, useEffect, useReducer } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import { setCookie } from 'nookies';
import { useNavigate } from 'react-router-dom';

import { FeaturesContext } from 'features';
import { IWorker } from 'types';

import { IGetNewTokensRequest, REFRESH_TOKEN } from '../schemas';
import { AuthContext, initialState } from './constants';
import { reducer } from './reducer';
import { GET_ME } from './schema';
import type { TSetTokens, IGetMeResponse } from './types';
import { ITokens, PermissionKey, TSetError } from './types';
import { getTokensFromStorage, removeTokensFromStorage } from './utils';

export const Auth: FC = ({ children }) => {
  const { clearFeatures } = useContext(FeaturesContext);

  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const setMe = useCallback(
    (me: IWorker) => {
      dispatch({
        type: 'SET_ME',
        data: { me },
      });
    },
    [dispatch]
  );

  const [loadMe, { loading: meLoading, client }] = useLazyQuery<IGetMeResponse>(
    GET_ME,
    {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'none',
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        if (!response || !response.me) return;

        const { permissions } = response.me;

        setMe(response.me);

        const permissionsSet = new Set<PermissionKey>();

        permissions.forEach(({ alias }) => permissionsSet.add(alias));

        setPermissions(permissionsSet);
      },
      onError: () => {
        removeTokensFromStorage();
        window.localStorage.removeItem('apollo-cache-persist');
        dispatch({ type: 'DROP_ALL_DATA' });
        clearFeatures();
        navigate('/login', { replace: true });
      },
    }
  );

  const setTokens: TSetTokens = useCallback((tokens) => {
    if (!tokens) return;

    setCookie(null, 'access_token', tokens.access_token, {
      maxAge: tokens.expires_in,
      secure: false,
    });
    setCookie(null, 'refresh_token', tokens.refresh_token, {
      maxAge: tokens.expires_in * 30,
      secure: false,
    });

    dispatch({ type: 'SET_TOKENS', data: { tokens } });
  }, []);

  const setError: TSetError = useCallback(
    (error) => dispatch({ type: 'SET_ERROR', data: { error } }),
    []
  );

  const [getNewTokensMutation] = useMutation<
    {
      refreshToken: ITokens;
    },
    IGetNewTokensRequest
  >(REFRESH_TOKEN, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!data.refreshToken) return;
      setTokens(data.refreshToken);
      return loadMe({
        context: {
          headers: {
            Authorization: `Bearer ${data.refreshToken.access_token}`,
          },
        },
      });
    },
    onError: () => {
      removeTokensFromStorage();
      window.localStorage.removeItem('apollo-cache-persist');
      dispatch({ type: 'DROP_ALL_DATA' });
      clearFeatures();
      navigate('/login', { replace: true });
    },
  });

  const logout = useCallback(() => {
    client.clearStore().then(() => {
      removeTokensFromStorage();
      dispatch({ type: 'DROP_ALL_DATA' });

      client.clearStore().then(() => {
        window.localStorage.removeItem('apollo-cache-persist');
        clearFeatures();
      });
    });
  }, [client]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  useEffect(() => {
    if (state.tokens?.access_token) {
      loadMe();

      return;
    } else if (state.tokens?.refresh_token) {
      getNewTokensMutation({
        variables: {
          input: {
            refresh_token: state.tokens.refresh_token,
          },
        },
      });
    } else {
      navigate('/login', { replace: true });
      dispatch({ type: 'AUTH_INITIALIZED' });
    }
  }, [state.tokens]);

  useEffect(() => {
    const storageTokensChecker = (): void => {
      if (document.visibilityState === 'visible') {
        const tokens = getTokensFromStorage();

        if (!tokens) {
          dispatch({ type: 'DROP_ALL_DATA' });
        }
      }
    };

    document.addEventListener('visibilitychange', storageTokensChecker);

    return () =>
      document.removeEventListener('visibilitychange', storageTokensChecker);
  }, []);

  const setPermissions = useCallback(
    (permissions: Set<PermissionKey>) =>
      dispatch({
        type: 'SET_PERMISSIONS',
        data: { permissions },
      }),
    [dispatch]
  );

  return (
    <AuthContext.Provider
      value={{
        data: state,
        actions: {
          setTokens,
          setMe,
          setError,
          logout,
          loadMe: {
            load: loadMe,
            loading: meLoading,
          },
          clearError,
          setPermissions,
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
